import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../ui/components/Input.jsx';
import Button from '../../ui/components/Button.jsx';
import { FormCheckbox } from '../../ui';
import { ErrorAlert } from '../../ui/components/Alert.jsx';
import { useTranslation } from '../../i18n';
import { useAuth } from '../hooks';
import AuthService from '../services/AuthService';
import { trackEvent } from '../../analytics/services/analyticsService';
import '../../ui/components/Form.css';

/**
 * RegisterForm - Registration form with validation and auto-login
 * Handles email, password, confirmPassword with strength validation
 */
const RegisterForm = function RegisterForm({ onSubmit, onSuccess, disabled = false, className = '', testId }) {
  const { t } = useTranslation();
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsPrivacyAccepted, setTermsPrivacyAccepted] = useState(false);
  const [touched, setTouched] = useState({
    fullName: false,
    email: false,
    password: false,
    confirmPassword: false,
    ageConfirmed: false,
    termsPrivacyAccepted: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    if (!password) return { score: 0, label: '', color: '#ccc' };
    
    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    score = Object.values(checks).filter(Boolean).length;
    
    if (score < 2) return { score, label: t('auth.register.passwordWeak'), color: '#e74c3c', checks };
    if (score < 4) return { score, label: t('auth.register.passwordMedium'), color: '#f39c12', checks };
    return { score, label: t('auth.register.passwordStrong'), color: '#27ae60', checks };
  }, [password, t]);

  const errors = useMemo(() => {
    const e = {};
    if (touched.fullName && (!fullName || fullName.trim().length < 2)) {
      e.fullName = t('validation.fullNameMinLength');
    }
    if (touched.email && (!email || !/.+@.+\..+/.test(email))) {
      e.email = t('validation.emailInvalid');
    }
    if (touched.password && (!password || password.length < 8)) {
      e.password = t('validation.passwordTooShort', { min: 8 });
    } else if (touched.password && passwordStrength.score < 3) {
      e.password = t('auth.register.passwordTooWeak');
    }
    if (touched.confirmPassword && password !== confirmPassword) {
      e.confirmPassword = t('validation.passwordsDoNotMatch');
    }
    if (touched.ageConfirmed && !ageConfirmed) {
      e.ageConfirmed = t('auth.register.ageConfirmationRequired');
    }
    if (touched.termsPrivacyAccepted && !termsPrivacyAccepted) {
      e.termsPrivacyAccepted = t('auth.register.termsPrivacyAcceptRequired');
    }
    return e;
  }, [fullName, email, password, confirmPassword, ageConfirmed, termsPrivacyAccepted, touched, passwordStrength.score, t]);

  const handleSubmit = useCallback(async (ev) => {
    ev.preventDefault();
    setTouched({
      fullName: true,
      email: true,
      password: true,
      confirmPassword: true,
      ageConfirmed: true,
      termsPrivacyAccepted: true
    });
    setSubmitError(null);
    setSubmitSuccess(false);
    if (Object.keys(errors).length > 0 || !ageConfirmed || !termsPrivacyAccepted) {
      return;
    }

    // External submit handler (optional)
    if (onSubmit) onSubmit({ fullName, email, password, confirmPassword });

    setSubmitting(true);
    try {
      const { data, error } = await AuthService.register({ fullName, email, password });
      
      if (error && error !== null) {
        // Handle duplicate email specifically
        if (error.message && (
          error.message.includes('email') || 
          error.message.includes('Email') ||
          error.message.includes('already exists') ||
          error.message.includes('already registered')
        )) {
          setSubmitError(t('auth.register.emailExists'));
        } else if (error.data && error.data.detail && error.data.detail.includes('email')) {
          setSubmitError(t('auth.register.emailExists'));
        } else if (error.data && error.data.errors && Array.isArray(error.data.errors)) {
          // Check if any error mentions email
          const emailError = error.data.errors.find(err => 
            err.toLowerCase().includes('email') || 
            err.toLowerCase().includes('имейл')
          );
          if (emailError) {
            setSubmitError(t('auth.register.emailExists'));
          } else {
            setSubmitError(error.message || error.data?.detail || t('errors.generic'));
          }
        } else {
          setSubmitError(error.message || error.data?.detail || t('errors.generic'));
        }
        return;
      }

      if (data) {
        trackEvent('sign_up', { method: 'email' });
        if (onSuccess) onSuccess(data);
        setSubmitSuccess(true);
      } else {
        setSubmitError(t('errors.generic'));
      }
    } catch (e) {
      setSubmitError(t('errors.network'));
    } finally {
      setSubmitting(false);
    }
  }, [errors, onSubmit, email, password, confirmPassword, ageConfirmed, termsPrivacyAccepted, authLogin, onSuccess, t]);

  return (
    <form className={'arbiwin-form arbiwin-form--card ' + className} onSubmit={handleSubmit} data-testid={testId || 'register-form'}>
      <div className="arbiwin-form__header">
        <h2 className="arbiwin-form__title">{t('auth.register.title')}</h2>
      </div>

      <div className="arbiwin-form__body">
        {submitError && (
          <div className="arbiwin-form__group">
            <ErrorAlert>
              {submitError}
            </ErrorAlert>
          </div>
        )}

        {submitSuccess && (
          <div className="arbiwin-form__group">
            <div className="arbiwin-form__success" style={{
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              color: '#155724',
              padding: '12px 16px',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '20px', marginRight: '8px' }}>✅</span>
                <strong>{t('auth.register.success')}</strong>
              </div>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                {t('auth.register.successMessage')}
              </p>
              <a 
                href="/login" 
                style={{
                  display: 'inline-block',
                  background: '#28a745',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => e.target.style.background = '#218838'}
                onMouseOut={(e) => e.target.style.background = '#28a745'}
              onClick={(event) => {
                event.preventDefault();
                navigate('/login');
              }}
              >
                {t('auth.register.goToLogin')}
              </a>
            </div>
          </div>
        )}

        {!submitSuccess && (
          <>
            <div className="arbiwin-form__group">
              <Input
                type="text"
                label={t('auth.register.fullName')}
                placeholder={t('auth.register.fullName')}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, fullName: true }))}
                required
                fullWidth
                error={errors.fullName}
              />
            </div>

            <div className="arbiwin-form__group">
              <Input
                type="email"
                label={t('auth.register.email')}
                placeholder={t('auth.register.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, email: true }))}
                required
                fullWidth
                error={errors.email}
              />
            </div>

            <div className="arbiwin-form__group">
              <Input
                type="password"
                label={t('auth.register.password')}
                placeholder={t('auth.register.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, password: true }))}
                required
                fullWidth
                showPassword
                error={errors.password}
              />
              
              {/* Password strength indicator */}
              {password && (
                <div className="arbiwin-form__password-strength" style={{ marginTop: '8px' }}>
                  <div className="arbiwin-form__strength-bar" style={{ 
                    height: '4px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${(passwordStrength.score / 5) * 100}%`,
                      backgroundColor: passwordStrength.color,
                      transition: 'all 0.3s ease'
                    }} />
                  </div>
                  <div className="arbiwin-form__strength-label" style={{
                    fontSize: '12px',
                    color: passwordStrength.color,
                    marginTop: '4px'
                  }}>
                    {passwordStrength.label}
                  </div>
                  
                  {/* Password requirements checklist */}
                  <div className="arbiwin-form__password-requirements" style={{ 
                    fontSize: '11px', 
                    color: '#666', 
                    marginTop: '8px' 
                  }}>
                    <div style={{ color: passwordStrength.checks?.length ? '#27ae60' : '#e74c3c' }}>
                      ✓ {t('auth.register.requirementLength')}
                    </div>
                    <div style={{ color: passwordStrength.checks?.lowercase ? '#27ae60' : '#e74c3c' }}>
                      ✓ {t('auth.register.requirementLowercase')}
                    </div>
                    <div style={{ color: passwordStrength.checks?.uppercase ? '#27ae60' : '#e74c3c' }}>
                      ✓ {t('auth.register.requirementUppercase')}
                    </div>
                    <div style={{ color: passwordStrength.checks?.numbers ? '#27ae60' : '#e74c3c' }}>
                      ✓ {t('auth.register.requirementNumbers')}
                    </div>
                    <div style={{ color: passwordStrength.checks?.special ? '#27ae60' : '#e74c3c' }}>
                      ✓ {t('auth.register.requirementSpecial')}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="arbiwin-form__group">
              <Input
                type="password"
                label={t('auth.register.confirmPassword')}
                placeholder={t('auth.register.confirmPassword')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, confirmPassword: true }))}
                required
                fullWidth
                showPassword
                error={errors.confirmPassword}
              />
            </div>

            <div className="arbiwin-form__group arbiwin-form__group--age-confirmation">
              <FormCheckbox
                checked={ageConfirmed}
                onChange={(e) => {
                  setAgeConfirmed(e.target.checked);
                  setTouched((s) => ({ ...s, ageConfirmed: true }));
                }}
                onBlur={() => setTouched((s) => ({ ...s, ageConfirmed: true }))}
                label={t('auth.register.ageConfirmation')}
                required
                error={errors.ageConfirmed}
                size="large"
                className="register-form__age-checkbox"
              />
            </div>

            <div className="arbiwin-form__group arbiwin-form__group--terms-privacy">
              <FormCheckbox
                checked={termsPrivacyAccepted}
                onChange={(e) => {
                  setTermsPrivacyAccepted(e.target.checked);
                  setTouched((s) => ({ ...s, termsPrivacyAccepted: true }));
                }}
                onBlur={() => setTouched((s) => ({ ...s, termsPrivacyAccepted: true }))}
                required
                error={errors.termsPrivacyAccepted}
                size="large"
                className="register-form__terms-checkbox"
              >
                {t('auth.register.termsPrivacyAcceptPrefix')}
                <a href="/static/terms" className="arbiwin-link" target="_blank" rel="noopener noreferrer">
                  {t('auth.register.termsLinkText')}
                </a>
                {t('auth.register.termsPrivacyAcceptAnd')}
                <a href="/static/privacy" className="arbiwin-link" target="_blank" rel="noopener noreferrer">
                  {t('auth.register.privacyLinkText')}
                </a>
                .
              </FormCheckbox>
            </div>
          </>
        )}
      </div>

      {!submitSuccess && (
        <div className="arbiwin-form__footer">
          <Button type="submit" variant="primary" size="large" disabled={disabled || submitting || !ageConfirmed || !termsPrivacyAccepted} fullWidth>
            {submitting ? t('feedback.loading') : t('auth.register.submit')}
          </Button>
          <p className="arbiwin-form__hint">
            {t('auth.register.hasAccount')} <a className="arbiwin-link" href="/login" onClick={(event) => {
              event.preventDefault();
              navigate('/login');
            }}>{t('auth.login.title')}</a>
          </p>
        </div>
      )}
    </form>
  );
};

RegisterForm.displayName = 'RegisterForm';

export default RegisterForm;
